import React, { useState, useEffect, useRef } from 'react';
import { EDGE_FUNCTION_URL } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);

  // Initialize session ID and load conversation history
  useEffect(() => {
    const savedSessionId = localStorage.getItem('prayer_coach_session_id');
    if (savedSessionId) {
      setSessionId(savedSessionId);
      loadConversationHistory(savedSessionId);
    } else {
      const newSessionId = uuidv4();
      setSessionId(newSessionId);
      localStorage.setItem('prayer_coach_session_id', newSessionId);
    }
  }, []);

  // Auto-scroll to prayer content when new messages arrive
  useEffect(() => {
    if (messages.length > 0) {
      // Find the last assistant message with prayer content
      const lastAssistantMessage = [...messages].reverse().find(msg => 
        msg.type === 'assistant' && msg.prayer
      );
      
      if (lastAssistantMessage) {
        // Scroll to the prayer content instead of the very bottom
        setTimeout(() => {
          const prayerElements = document.querySelectorAll('.prayer-content');
          if (prayerElements.length > 0) {
            prayerElements[prayerElements.length - 1].scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 100);
      } else {
        // Fallback to scrolling to bottom
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages]);

  // Load conversation history from localStorage
  const loadConversationHistory = (sessionId) => {
    const savedMessages = localStorage.getItem(`prayer_coach_messages_${sessionId}`);
    if (savedMessages) {
      try {
        setMessages(JSON.parse(savedMessages));
      } catch (error) {
        console.error('Error loading conversation history:', error);
      }
    }
  };

  // Save conversation history to localStorage
  const saveConversationHistory = (sessionId, messages) => {
    try {
      localStorage.setItem(`prayer_coach_messages_${sessionId}`, JSON.stringify(messages));
    } catch (error) {
      console.error('Error saving conversation history:', error);
    }
  };

  // Client-side guardrail for prayer requests - now more comprehensive
  const isPrayerRequest = (question) => {
    const lowerQuestion = question.toLowerCase();
    
    // Explicit prayer keywords
    const prayerKeywords = [
      'pray', 'prayer', 'praying', 'prayed'
    ];
    
    // Struggle/problem indicators
    const struggleKeywords = [
      'struggling', 'struggle', 'struggles',
      'difficult', 'difficulty', 'difficulties',
      'challenging', 'challenge', 'challenges',
      'hard time', 'hard times', 'tough time', 'tough times',
      'going through', 'dealing with', 'facing',
      'battling', 'fighting', 'fighting against',
      'overwhelmed', 'overwhelming', 'overwhelm',
      'stressed', 'stress', 'stressing',
      'anxious', 'anxiety', 'worried', 'worry', 'worries',
      'afraid', 'fear', 'fears', 'scared', 'scary',
      'confused', 'confusion', 'lost', 'losing',
      'broken', 'hurt', 'hurting', 'pain', 'painful',
      'sad', 'sadness', 'depressed', 'depression',
      'lonely', 'loneliness', 'alone', 'isolated',
      'angry', 'anger', 'frustrated', 'frustration',
      'hopeless', 'hopelessness', 'desperate', 'desperation',
      'stuck', 'trapped', 'helpless', 'helplessness'
    ];
    
    // Life situation keywords
    const lifeSituationKeywords = [
      'job', 'work', 'career', 'employment', 'unemployed',
      'money', 'financial', 'finances', 'bills', 'debt',
      'relationship', 'relationships', 'marriage', 'divorce',
      'family', 'children', 'kids', 'parenting',
      'health', 'sick', 'illness', 'disease', 'medical',
      'addiction', 'addicted', 'recovery', 'sober',
      'grief', 'grieving', 'loss', 'death', 'died',
      'faith', 'belief', 'doubt', 'doubts', 'questioning',
      'purpose', 'meaning', 'direction', 'guidance',
      'decision', 'decisions', 'choice', 'choices',
      'future', 'tomorrow', 'next', 'ahead',
      'past', 'regret', 'regrets', 'mistakes',
      'forgiveness', 'forgive', 'forgiving',
      'peace', 'peaceful', 'calm', 'serenity',
      'healing', 'heal', 'healed', 'recovery',
      'strength', 'strong', 'courage', 'brave',
      'wisdom', 'wise', 'understanding', 'understand',
      'patience', 'patient', 'endurance', 'perseverance',
      'hope', 'hopeful', 'optimistic', 'positive',
      'love', 'loving', 'compassion', 'kindness',
      'joy', 'happy', 'happiness', 'blessed', 'blessing'
    ];
    
    // Help/guidance request keywords
    const helpKeywords = [
      'help', 'help me', 'need help', 'need guidance',
      'guide me', 'show me', 'teach me', 'advice',
      'support', 'comfort', 'encourage', 'encouragement',
      'pray for', 'praying for', 'prayer for'
    ];
    
    // Check for explicit prayer requests
    if (prayerKeywords.some(keyword => lowerQuestion.includes(keyword))) {
      return true;
    }
    
    // Check for help/guidance requests
    if (helpKeywords.some(keyword => lowerQuestion.includes(keyword))) {
      return true;
    }
    
    // Check for struggle/problem indicators
    if (struggleKeywords.some(keyword => lowerQuestion.includes(keyword))) {
      return true;
    }
    
    // Check for life situation keywords
    if (lifeSituationKeywords.some(keyword => lowerQuestion.includes(keyword))) {
      return true;
    }
    
    // Check for common sentence starters that indicate personal struggles
    const personalStarters = [
      'i am', 'i\'m', 'i have', 'i need', 'i want', 'i feel',
      'i can\'t', 'i don\'t', 'i won\'t', 'i shouldn\'t',
      'i wish', 'i hope', 'i pray', 'i ask',
      'my life', 'my situation', 'my problem', 'my issue',
      'everything', 'nothing', 'always', 'never',
      'why does', 'why is', 'why am', 'why can\'t',
      'how do', 'how can', 'how will', 'how should'
    ];
    
    if (personalStarters.some(starter => lowerQuestion.startsWith(starter))) {
      return true;
    }
    
    return false;
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inputValue.trim()) return;
    
    // Prevent multiple submissions
    if (loading) return;

    const question = inputValue.trim();
    
    // Client-side guardrail
    if (!isPrayerRequest(question)) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'user',
        content: question,
        timestamp: new Date()
      }, {
        id: Date.now() + 1,
        type: 'assistant',
        content: "I'm here to help you pray about what you're going through. You can share your struggles, concerns, or challenges with me, and I'll help you find the right words to pray about them.",
        isGuardrail: true,
        timestamp: new Date()
      }]);
      
      setInputValue('');
      return;
    }

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: question,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);

    try {
      // Get last 2-5 conversation pairs for context
      const recentMessages = messages.slice(-10); // Last 10 messages (5 pairs)
      const conversationHistory = [];
      
      for (let i = 0; i < recentMessages.length; i += 2) {
        if (recentMessages[i] && recentMessages[i + 1]) {
          conversationHistory.push({
            question: recentMessages[i].content,
            answer: recentMessages[i + 1].content
          });
        }
      }

      // Call Edge Function
      console.log('Making request to:', EDGE_FUNCTION_URL);
      
      const response = await fetch(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpybXVob2VhYW1td3Z3Z2RpYmRuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTc2OTAxODYsImV4cCI6MjA3MzI2NjE4Nn0.KOixWHsUgD4N-Fh9vsfVzo48k4N7WBFgly9uHpxS4hc`
        },
        body: JSON.stringify({
          question,
          conversation_history: conversationHistory,
          session_id: sessionId
        })
      });
      
      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      // Add assistant response
      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: data.acknowledgment,
        prayer: data.prayer,
        scriptureReferences: data.scripture_references || [],
        scriptureTexts: data.scripture_texts || [],
        safetyNote: data.safety_note,
        timestamp: new Date()
      };

      const newMessages = [...messages, userMessage, assistantMessage];
      setMessages(newMessages);
      saveConversationHistory(sessionId, newMessages);

    } catch (error) {
      console.error('Error calling Edge Function:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: `I'm sorry, I'm having trouble connecting right now. Error: ${error.name} - ${error.message}`,
        isError: true,
        timestamp: new Date()
      };

      const newMessages = [...messages, userMessage, errorMessage];
      setMessages(newMessages);
      saveConversationHistory(sessionId, newMessages);
    } finally {
      setLoading(false);
    }
  };


  // Clear conversation
  const clearConversation = () => {
    setMessages([]);
    localStorage.removeItem(`prayer_coach_messages_${sessionId}`);
  };

  return (
    <div className="chat-container">
      {/* Controls */}
      <div className="user-info">
        <button 
          className="auth-button" 
          onClick={clearConversation}
          style={{ padding: '8px 16px', fontSize: '0.9rem' }}
        >
          Clear Chat
        </button>
      </div>

      {/* Messages */}
      <div className="messages">
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 20px' }}>
            <h3 style={{ color: '#e2e8f0', marginBottom: '16px' }}>Welcome to your prayer space</h3>
            <p style={{ marginBottom: '16px' }}>Share what's on your heart, and I'll help you pray about it using God's Word.</p>
            <p style={{ marginTop: '10px', fontSize: '0.9rem', color: '#cbd5e1' }}>
              Try: "I'm struggling with finding a job" or "I need help with my anxiety" or "I'm feeling lost in life"
            </p>
            <p style={{ marginTop: '5px', fontSize: '0.8rem', color: '#64748b' }}>
              💡 Tip: Press Enter to submit your prayer request
            </p>
          </div>
        )}

        {messages.map((message) => (
          <div key={message.id} className={`message ${message.type}`}>
            <div className="message-header">
              {message.type === 'user' ? 'You' : 'Prayer Flow'}
            </div>
            
            {message.isGuardrail ? (
              <div className="guardrail-message">
                {message.content}
              </div>
            ) : message.isError ? (
              <div style={{ color: '#dc3545', fontStyle: 'italic' }}>
                {message.content}
              </div>
            ) : (
              <>
                <div>{message.content}</div>
                
                {message.prayer && (
                  <div className="prayer-content">
                    {message.prayer}
                  </div>
                )}
                
                {message.scriptureReferences && message.scriptureReferences.length > 0 && (
                  <div className="scripture-references">
                    <h4>Scripture References:</h4>
                    {message.scriptureTexts && message.scriptureTexts.length > 0 ? (
                      <div className="scripture-texts">
                        {message.scriptureTexts.map((scripture, index) => (
                          <div key={index} className="scripture-passage">
                            <div className="scripture-reference">{scripture.reference}</div>
                            <div className="scripture-text">"{scripture.text}"</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <ul className="scripture-list">
                        {message.scriptureReferences.map((ref, index) => (
                          <li key={index}>{ref}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                
                {message.safetyNote && (
                  <div className="safety-note">
                    {message.safetyNote}
                  </div>
                )}
              </>
            )}
          </div>
        ))}

        {loading && (
          <div className="message assistant">
            <div className="loading">
              <div className="spinner"></div>
              Praying with you...
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Form */}
      <form onSubmit={handleSubmit} className="input-section">
        <div className="input-container">
          <textarea
            className="textarea"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Share what's on your heart... (Press Enter to pray)"
            disabled={loading}
            rows="3"
          />
        </div>
      </form>
    </div>
  );
};

export default ChatInterface;
